import { apiSlice } from '../../../store/api/apiSlice';
import type {
  AvailabilitySettingsRequest,
  AvailabilitySettingsResponse,
} from './availabilityTypes';

let nextAvailabilitySaveSequence = 0;
let lastAppliedAvailabilitySaveSequence = 0;

function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export function availabilityConflictData(
  reason: unknown
): AvailabilitySettingsResponse | undefined {
  const candidate = reason as {
    status?: unknown;
    data?: unknown;
    error?: { status?: unknown; data?: unknown };
  };
  const error = candidate?.error ?? candidate;
  const data = error?.data;
  if (
    error?.status !== 409 ||
    typeof data !== 'object' ||
    data === null ||
    typeof (data as AvailabilitySettingsResponse).time_zone !== 'string' ||
    !Array.isArray((data as AvailabilitySettingsResponse).days)
  ) {
    return undefined;
  }
  return data as AvailabilitySettingsResponse;
}

export const calendarAvailabilityApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCalendarAvailability: builder.query<AvailabilitySettingsResponse, void>({
      query: () => {
        const timeZone = browserTimeZone();
        return timeZone
          ? { url: '/calendar/availability', params: { browser_time_zone: timeZone } }
          : '/calendar/availability';
      },
      providesTags: ['CalendarSettings'],
    }),
    updateCalendarAvailability: builder.mutation<
      AvailabilitySettingsResponse,
      AvailabilitySettingsRequest
    >({
      query: (body) => ({
        url: '/calendar/availability',
        method: 'PUT',
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const saveSequence = nextAvailabilitySaveSequence + 1;
        nextAvailabilitySaveSequence = saveSequence;
        try {
          const { data } = await queryFulfilled;
          // A landed save changes what members are offered; refresh any open
          // Member View. Not CalendarSettings: that would refetch this editor's
          // own query and race its save sequencing.
          dispatch(apiSlice.util.invalidateTags(['MemberPreview']));
          if (saveSequence < lastAppliedAvailabilitySaveSequence) {
            return;
          }
          lastAppliedAvailabilitySaveSequence = saveSequence;
          dispatch(
            calendarAvailabilityApi.util.updateQueryData(
              'getCalendarAvailability',
              undefined,
              () => data
            )
          );
        } catch (reason) {
          const data = availabilityConflictData(reason);
          if (!data || saveSequence < lastAppliedAvailabilitySaveSequence) {
            return;
          }
          lastAppliedAvailabilitySaveSequence = saveSequence;
          dispatch(
            calendarAvailabilityApi.util.updateQueryData(
              'getCalendarAvailability',
              undefined,
              () => data
            )
          );
        }
      },
    }),
  }),
});

export const { useGetCalendarAvailabilityQuery, useUpdateCalendarAvailabilityMutation } =
  calendarAvailabilityApi;
